import { inject, Injectable } from '@angular/core';
import {
	collectionCount,
	FIRESTORE,
	ORGANIZATION_ID,
} from '@context/frontend/common';
import { Fetchable, PaginatedObject } from '@context/frontend/pagination';
import { ChemicalRevision } from '@context/shared/types/chemical';
import {
	collection,
	collectionGroup,
	CollectionReference,
	documentId,
	DocumentSnapshot,
	endAt,
	getDocs,
	limit,
	orderBy,
	query,
	startAfter,
	startAt,
} from 'firebase/firestore';
import { firstValueFrom } from 'rxjs';
import { ChemicalService } from './chemical.service';

@Injectable({ providedIn: 'root' })
export class ChemicalRevisionService implements Fetchable<ChemicalRevision> {
	private readonly firestore = inject(FIRESTORE);
	private readonly organizationId = inject(ORGANIZATION_ID);

	async fetch(
		paginated?: PaginatedObject<ChemicalRevision>,
	): Promise<PaginatedObject<ChemicalRevision>> {
		if (!paginated) paginated = new PaginatedObject<ChemicalRevision>();
		if (!paginated?.parentRef)
			throw new Error('Parent chemical reference is required');

		const revisionCollection = collection(
			paginated.parentRef,
			ChemicalService.RevisionCollectionId,
		) as CollectionReference<ChemicalRevision, ChemicalRevision>;

		if (!paginated.totalElements) {
			paginated.totalElements = await firstValueFrom(
				collectionCount(
					query(revisionCollection, ...paginated.constraints),
				),
			);
		}

		// if the object already contains the data, don't fetch again
		if (paginated.page) return paginated;

		const constraints = [...paginated.constraints];
		const lastDoc = paginated.previousPage?.last ?? null;
		if (lastDoc) constraints.push(startAfter(lastDoc));
		if (paginated.pageSize) constraints.push(limit(paginated.pageSize));

		const snapshot = await getDocs(
			query(revisionCollection, ...constraints),
		);

		let last: DocumentSnapshot | null = null;
		const items = snapshot.docs.map((d) => {
			last = d;
			return { data: d.data() as ChemicalRevision, ref: d.ref, id: d.id };
		});

		paginated.pages = Object.assign({}, paginated.pages, {
			[paginated.currentPage as number]: { last, items },
		});

		return paginated;
	}

	/**
	 * Fetches all paginated revisions across all chemicals using collectionGroup.
	 * Requires organizationId to be available.
	 */
	async fetchAll(
		paginated?: PaginatedObject<ChemicalRevision>,
	): Promise<PaginatedObject<ChemicalRevision>> {
		if (!paginated) paginated = new PaginatedObject<ChemicalRevision>();

		const organizationId = this.organizationId.value;
		if (!organizationId) {
			throw new Error('Organization ID is required for fetchAll');
		}
		const revisionQuery = query(
			collectionGroup(
				this.firestore,
				ChemicalService.RevisionCollectionId,
			) as CollectionReference<ChemicalRevision>,
			orderBy(documentId()),
			startAt(`organizations/${organizationId}/`),
			endAt(`organizations/${organizationId}/\uf8ff`),
		);

		if (!paginated.totalElements) {
			paginated.totalElements = await firstValueFrom(
				collectionCount(query(revisionQuery, ...paginated.constraints)),
			);
		}

		// if the object already contains the data, don't fetch again
		if (paginated.page) return paginated;

		const constraints = [...paginated.constraints];
		const lastDoc = paginated.previousPage?.last ?? null;
		if (lastDoc) constraints.push(startAfter(lastDoc));
		if (paginated.pageSize) constraints.push(limit(paginated.pageSize));

		const snapshot = await getDocs(query(revisionQuery, ...constraints));

		let last: DocumentSnapshot | null = null;
		const items = snapshot.docs.map((d) => {
			last = d;
			return { data: d.data() as ChemicalRevision, ref: d.ref, id: d.id };
		});

		paginated.pages = Object.assign({}, paginated.pages, {
			[paginated.currentPage as number]: { last, items },
		});

		return paginated;
	}
}
